import React, { FC, useMemo, useState } from 'react';
import ActionList from '@components/ActionsMenu/ActionList';
import { IActionItem } from '@components/ActionsMenu/types';
import useStore from '@components/MainStage/store';
import { Modes } from '@constants/canvas/layers';
import { useProject } from '@context/Project/ProjectProvider';
import { useToast } from '@context/Toast/ToastProvider';
import useAssets from '@hooks/useAssets';
import useSaveEntitiesResponse from '@hooks/useSaveEntitiesResponse';
import { useUpdateNodesActionMutation } from '@store/services/nodes';
import { NodeDTO } from '@store/services/nodes/types';
import {
  getNodeActionChangePlan,
  NodeActionChangePlan,
  NodeActionConfig,
} from '@utils/nodeActions';

import { NodeActionConfirmModal } from './NodeActionConfirmModal';

const DISABLED_STATUS_TOOLTIP =
  'This status cannot be applied to the selected entities.';

type Props = {
  config: NodeActionConfig;
  nodeIds: string[];
  onCloseMenu: () => void;
};

export const NodeActionSubmenu: FC<Props> = ({
  config,
  nodeIds,
  onCloseMenu,
}) => {
  const { getNodeById } = useStore();
  const {
    toolbox: { mode },
  } = useProject();
  const resolvedMode = mode ?? Modes.BusinessAlignment;
  const { selectedAssetId } = useAssets();
  const { showToast } = useToast();
  const { saveResponseToRedux } = useSaveEntitiesResponse();
  const [updateNodesAction] = useUpdateNodesActionMutation();

  const [pendingStatus, setPendingStatus] = useState<string | null>(null);

  const nodes = useMemo(
    () =>
      nodeIds
        .map((id) => getNodeById(id)?.data?.dto as NodeDTO | undefined)
        .filter((dto): dto is NodeDTO => Boolean(dto)),
    [nodeIds, getNodeById],
  );

  const planByStatus = useMemo(() => {
    const acc: Record<string, NodeActionChangePlan> = {};
    (config.statusOptions ?? []).forEach((option) => {
      acc[option.value] = getNodeActionChangePlan(
        nodes,
        resolvedMode,
        option.value,
        config,
      );
    });
    return acc;
  }, [nodes, resolvedMode, config]);

  const apply = async (value: string) => {
    const response = await updateNodesAction({
      nodeIds,
      actionType: config.actionType,
      value,
      mode: resolvedMode,
      assetId: selectedAssetId || undefined,
    }).unwrap();

    saveResponseToRedux(response);
    showToast(config.successMessage, 'success');
  };

  const notSetCount = (plan: NodeActionChangePlan) =>
    plan.blocked.length + plan.unchanged.length;

  const handleSelect = async (value: string) => {
    const plan = planByStatus[value];
    if (config.immediateApplyWhenAllEligible && notSetCount(plan) === 0) {
      await apply(value);
      if (!config.keepMenuOpenOnApply) {
        onCloseMenu();
      }
      return;
    }
    setPendingStatus(value);
  };

  const items: IActionItem[] = (config.statusOptions ?? []).map((option) => {
    const disabled = planByStatus[option.value].toChange.length === 0;
    return {
      text: option.label,
      disabled,
      tooltip: disabled ? DISABLED_STATUS_TOOLTIP : undefined,
      onClick: () => handleSelect(option.value),
    };
  });

  const pendingPlan = pendingStatus ? planByStatus[pendingStatus] : null;
  const skipped = pendingPlan
    ? [...pendingPlan.blocked, ...pendingPlan.unchanged].map((u) => u.node)
    : [];

  return (
    <>
      <ActionList items={items} />

      <NodeActionConfirmModal
        open={pendingStatus !== null}
        title={config.menuLabel}
        text={config.menuText}
        toChange={pendingPlan?.toChange ?? []}
        toChangeTitle={config.menuToChangeTitle}
        skipped={skipped}
        toSkipTitle={config.menuToSkipTitle}
        confirmName={config.menuConfirmName}
        onBack={() => setPendingStatus(null)}
        onConfirm={async () => {
          if (pendingStatus) {
            await apply(pendingStatus);
          }
          setPendingStatus(null);
          if (!config.keepMenuOpenOnApply) {
            onCloseMenu();
          }
        }}
      />
    </>
  );
};
